import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Sparkles } from 'lucide-react';
import { TierBadge } from './TierBadge';
import { getTier } from '../utils/tier';
import { Avatar, AvatarFallback } from './Avatar';
import { Badge } from './Badge';
import { EmiratiBadge } from './EmiratiBadge';
import { PlatformIcon } from './PlatformIcon';
import { RatingStars } from './RatingStars';
import type { Expert } from '../types/expert';
import { averageRating, engagementCount, formatAED, formatCompact, getInitials, scoreColor, totalPaid } from '../utils/expert';
type ExpertCardProps = {
  expert: Expert;
  index: number;
};
const availabilityStyle: Record<Expert['availability'], string> = {
  Available: 'bg-emerald-500',
  Limited: 'bg-amber-500',
  Booked: 'bg-red-500'
};
export function ExpertCard({
  expert,
  index
}: ExpertCardProps) {
  const rating = averageRating(expert);
  const verified = expert.expertise.filter(e => e.source === 'Verified').slice(0, 2);
  const suggested = expert.expertise.filter(e => e.source === 'AI suggested').length;
  return <motion.article initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.25,
    delay: index * 0.04
  }} data-id="element-258" data-mp="3k9dl9efgr4lb">
      <Link to={`/experts/${expert.id}`} className="group flex h-full flex-col rounded-xl border bg-card p-5 transition-colors hover:border-foreground/20 hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50" data-id="element-259" data-mp="3tb4omcmj6zc0">
        <div className="flex items-start gap-3" data-id="element-260" data-mp="30kcx3sgeuyv0">
          <Avatar size="lg" data-id="element-261" data-mp="35kr7hh5hikw7">
            <AvatarFallback className="font-medium" data-id="element-262" data-mp="35khhvuocqfr1">{getInitials(expert.name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1" data-id="element-263" data-mp="3aellc3yes5nd">
            <div className="flex items-center gap-2" data-id="element-264" data-mp="2cxwq4t3ys1my">
              <h3 className="truncate text-sm font-semibold" data-id="element-265" data-mp="2cv22kspnm0pb">{expert.name}</h3>
              {expert.isEmirati && <EmiratiBadge compact data-id="element-266" data-mp="2htv7vxuselee" />}
            </div>
            <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground" data-id="element-267" data-mp="3l63po4lfk94y">{expert.title}</p>
            <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground" data-id="element-268" data-mp="3g6zgvf2f4df5">
              <MapPin className="size-3" aria-hidden="true" data-id="element-269" data-mp="3tzp1pg67ncao" />
              {expert.location}
              <span aria-hidden="true" data-id="element-270" data-mp="3pxc093yhy0jp">·</span>
              <span className={`size-1.5 rounded-full ${availabilityStyle[expert.availability]}`} aria-hidden="true" data-id="element-271" data-mp="3pynbfou9uaku" />
              {expert.availability}
            </p>
          </div>
          <TierBadge tier={getTier(expert)} data-id="element-272" data-mp="2xar7bz2dhge1" />
        </div>

        <div className="mt-4 flex flex-wrap gap-1.5" data-id="element-273" data-mp="3b9h4tft3ti2x">
          {verified.map(f => <Badge key={f.name} variant="secondary" data-id="element-274" data-mp="36a4iqxa24f1e">{f.name}</Badge>)}
          {suggested > 0 && <Badge variant="outline" className="gap-1" data-id="element-275" data-mp="qi75k5zsvtq3">
              <Sparkles className="size-3" aria-hidden="true" data-id="element-276" data-mp="qktd45moy98b" />+{suggested} AI suggested
            </Badge>}
        </div>

        <dl className="mt-5 grid grid-cols-3 gap-3 border-t pt-4" data-id="element-277" data-mp="u0wbyf11xul3">
          <div data-id="element-278" data-mp="u2qwhs8evkon">
            <dt className="text-[11px] text-muted-foreground" data-id="element-279" data-mp="y29h0mwbltqy">Engagements</dt>
            <dd className="mt-0.5 text-sm font-semibold tabular-nums" data-id="element-280" data-mp="y053dd8mdgdh">{engagementCount(expert)}</dd>
          </div>
          <div data-id="element-281" data-mp="p1jn5oqmedfv">
            <dt className="text-[11px] text-muted-foreground" data-id="element-282" data-mp="p1bcm3hyzk7u">Rating</dt>
            <dd className="mt-0.5 flex items-center gap-1 text-sm font-semibold tabular-nums" data-id="element-283" data-mp="p3e8v59pu9l0">
              {rating.toFixed(1)}
              <RatingStars value={rating} size={10} data-id="element-284" data-mp="1hqqkwli92z02" />
            </dd>
          </div>
          <div data-id="element-285" data-mp="131cdhiy8irnb">
            <dt className="text-[11px] text-muted-foreground" data-id="element-286" data-mp="131ugcyfek447">Social score</dt>
            <dd className={`mt-0.5 text-sm font-semibold tabular-nums ${scoreColor(expert.social.overallScore)}`} data-id="element-287" data-mp="180ffeuix4hr2">
              {expert.social.overallScore}/100
            </dd>
          </div>
        </dl>

        <div className="mt-4 flex items-end justify-between gap-2" data-id="element-288" data-mp="20ggfp8m0jkrz">
          <div data-id="element-289" data-mp="20it6mb9r15h6">
            <p className="text-[11px] text-muted-foreground" data-id="element-290" data-mp="bux7f8t62trc">Day rate</p>
            <p className="text-sm font-semibold tabular-nums" data-id="element-291" data-mp="bw8d5e66abl3">{formatAED(expert.dailyRate)}</p>
            <p className="text-[11px] text-muted-foreground" data-id="element-292" data-mp="7s0jd2kuy5g0">Paid to date {formatAED(totalPaid(expert))}</p>
          </div>
          <div className="flex -space-x-1.5" data-id="element-293" data-mp="2w20fuiatfmz">
            {expert.social.accounts.map(a => <span key={a.platform} title={`${a.platform} · ${formatCompact(a.followers)} followers`} className="rounded-md ring-2 ring-card" data-id="element-294" data-mp="3u2v2t1otvkt3">
                <PlatformIcon platform={a.platform} className="size-6" data-id="element-295" data-mp="qkuoz5wlcl7k" />
              </span>)}
          </div>
        </div>
      </Link>
    </motion.article>;
}